import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { Book, QuestionGroup, Question } from '../types/database'
import { Sidebar } from './Sidebar'
import { useAuth } from '../contexts/AuthContext'

type BookWithGroups = Book & {
  question_groups: (QuestionGroup & {
    questions: Question[]
  })[]
}

export function HierarchicalQuestionNav() {
  const { roles } = useAuth()
  const navigate = useNavigate()
  const [books, setBooks] = useState<BookWithGroups[]>([])
  const [expandedBooks, setExpandedBooks] = useState<Set<number>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchHierarchy = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('books')
        .select(`
          *,
          question_groups (
            *,
            questions (id, name, display_order)
          )
        `)
        .order('display_order', { ascending: true })

      if (!roles) {
        query = query.lt('status', 0)
      } else {
        query = query.gte('status', 0)
      }

      const { data, error } = await query

      if (error) throw error

      // Sort question_groups and questions by display_order
      const sortedData = (data || []).map(book => ({
        ...book,
        question_groups: (book.question_groups || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map(group => ({
            ...group,
            questions: (group.questions || []).sort((a, b) => a.display_order - b.display_order)
          }))
      }))

      setBooks(sortedData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch hierarchy')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHierarchy()
  }, [])

  const toggleBook = (bookId: number) => {
    const newExpanded = new Set(expandedBooks)
    if (newExpanded.has(bookId)) {
      newExpanded.delete(bookId)
    } else {
      newExpanded.add(bookId)
    }
    setExpandedBooks(newExpanded)
  }

  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId)
    } else {
      newExpanded.add(groupId)
    }
    setExpandedGroups(newExpanded)
  }

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-5xl mx-auto">
          <header className="mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
              계층 구조 탐색
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              교재 &gt; 문제 그룹 &gt; 문제의 계층 구조로 탐색합니다
            </p>
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {books.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">folder_off</span>
                <p className="text-gray-500">등록된 교재가 없습니다.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {books.map((book) => (
                  <div key={book.id}>
                    <div
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => toggleBook(book.id)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleBook(book.id)
                          }}
                        >
                          <span className="material-symbols-outlined text-base">
                            {expandedBooks.has(book.id) ? 'expand_more' : 'chevron_right'}
                          </span>
                        </button>
                        <span className="material-symbols-outlined text-primary text-xl">book</span>
                        <div>
                          <h3 className="font-medium text-gray-900">{book.name}</h3>
                          {book.description && (
                            <p className="text-xs text-gray-500">{book.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">
                          {book.question_groups.length}개 그룹
                        </span>
                        {roles.includes('moderator') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/books/${book.id}/groups`)
                            }}
                            className="text-primary hover:text-primary/80 text-sm font-medium"
                          >
                            관리
                          </button>
                        )}
                      </div>
                    </div>

                    {expandedBooks.has(book.id) && (
                      <div className="bg-gray-50 pl-12 divide-y divide-gray-200">
                        {book.question_groups.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            문제 그룹이 없습니다.
                          </div>
                        ) : (
                          book.question_groups.map((group) => (
                            <div key={group.id}>
                              <div
                                className="flex items-center justify-between px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors"
                                onClick={() => toggleGroup(group.id)}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <button
                                    className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      toggleGroup(group.id)
                                    }}
                                  >
                                    <span className="material-symbols-outlined text-base">
                                      {expandedGroups.has(group.id) ? 'expand_more' : 'chevron_right'}
                                    </span>
                                  </button>
                                  <span className="material-symbols-outlined text-blue-600 text-lg">folder</span>
                                  <div>
                                    <h4 className="font-medium text-gray-800">{group.name}</h4>
                                    {group.description && (
                                      <p className="text-xs text-gray-500">{group.description}</p>
                                    )}
                                  </div>
                                </div>
                                <span className="text-sm text-gray-500">
                                  {group.questions.length}개 문제
                                </span>
                              </div>

                              {expandedGroups.has(group.id) && (
                                <div className="bg-white pl-12 divide-y divide-gray-100">
                                  {group.questions.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500">
                                      문제가 없습니다.
                                    </div>
                                  ) : (
                                    group.questions.map((question) => (
                                      <div
                                        key={question.id}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => navigate(`/questions?questionId=${question.id}`)}
                                      >
                                        <span className="material-symbols-outlined text-green-600 text-base">quiz</span>
                                        <span className="text-sm text-gray-700">{question.name}</span>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
